import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Star, ExternalLink, Check, Search, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

const API_BASE = 'https://spotsvc.up.railway.app';

interface Suggestion {
  place_id: string;
  text: string;
  secondary_text: string;
}

interface PlaceDetails {
  name: string;
  formatted_address: string;
  rating: number | null;
  user_rating_count: number | null;
  website_uri: string | null;
  editorial_summary: string | null;
  business_status: string | null;
  price_level: string | null;
  photos: string[];
}

export default function SuggestSpot() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefilledPlaceId = searchParams.get('place_id');

  const [step, setStep] = useState<0 | 1 | 2>(prefilledPlaceId ? 1 : 0);

  // Step 0 — Search
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [searching, setSearching] = useState(false);

  // Step 1 — Preview
  const [placeId, setPlaceId] = useState(prefilledPlaceId || '');
  const [place, setPlace] = useState<PlaceDetails | null>(null);
  const [loadingPlace, setLoadingPlace] = useState(false);
  const [suggesterName, setSuggesterName] = useState('');
  const [suggesterEmail, setSuggesterEmail] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Debounced autocomplete
  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/admin/google-autocomplete?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Fetch place details
  useEffect(() => {
    if (step !== 1 || !placeId) return;
    setLoadingPlace(true);
    setPlace(null);
    fetch(`${API_BASE}/admin/google-place/${placeId}`)
      .then(r => r.json())
      .then(data => setPlace(data.place))
      .catch(() => {})
      .finally(() => setLoadingPlace(false));
  }, [step, placeId]);

  const handleSelect = (s: Suggestion) => {
    setPlaceId(s.place_id);
    setStep(1);
    setSuggestions([]);
  };

  const handleBack = () => {
    if (prefilledPlaceId) {
      navigate('/spots');
      return;
    }
    setStep(0);
    setPlace(null);
    setPlaceId('');
    setQuery('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!place || submitting) return;
    setSubmitting(true);
    setSubmitError('');

    const body: Record<string, string> = {
      google_place_id: placeId,
      place_name: place.name,
      place_address: place.formatted_address,
    };
    if (suggesterName.trim()) body.suggester_name = suggesterName.trim();
    if (suggesterEmail.trim()) body.suggester_email = suggesterEmail.trim();
    if (note.trim()) body.note = note.trim();

    try {
      const res = await fetch(`${API_BASE}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.status === 201) {
        setStep(2);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(0);
    setQuery('');
    setSuggestions([]);
    setPlaceId('');
    setPlace(null);
    setSuggesterName('');
    setSuggesterEmail('');
    setNote('');
    setSubmitError('');
    if (prefilledPlaceId) {
      navigate('/spots/suggest', { replace: true });
    }
  };

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-input bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-lg mx-auto px-4 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">Suggest a Spot</h1>
          <p className="text-muted-foreground text-sm mt-1">Know a great place to work from? Let us know.</p>
        </div>

        {/* Step 0 — Search */}
        {step === 0 && (
          <div>
            <div className="relative">
              <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Tell us your favourite spot…"
                autoFocus
                className={cn(inputClass, 'pl-10')}
              />
              {searching && (
                <Loader2 className="absolute right-3 top-3.5 w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </div>
            {suggestions.length > 0 && (
              <div className="mt-2 rounded-xl border border-input bg-card overflow-hidden shadow-sm">
                {suggestions.map(s => (
                  <button
                    key={s.place_id}
                    onClick={() => handleSelect(s)}
                    className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-input last:border-b-0"
                  >
                    <div className="text-sm font-semibold text-foreground">{s.text}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.secondary_text}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1 — Preview + Form */}
        {step === 1 && (
          <div>
            {loadingPlace ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-7 h-7 animate-spin text-primary" />
              </div>
            ) : place ? (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Place card */}
                <div className="rounded-2xl border border-input bg-card overflow-hidden shadow-sm">
                  {/* Photos hidden — API now returns photo_references (tokens), not URLs */}
                  <div className="p-5 space-y-2">
                    <h2 className="text-xl font-bold text-foreground">{place.name}</h2>
                    <p className="text-sm text-muted-foreground">{place.formatted_address}</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      {place.rating != null && (
                        <div className="flex items-center gap-1 text-sm text-foreground">
                          <Star className="w-4 h-4 fill-current text-primary" />
                          <span className="font-semibold">{place.rating}</span>
                        </div>
                      )}
                      {place.price_level && (
                        <span className="text-sm text-muted-foreground">{place.price_level}</span>
                      )}
                      {place.website_uri && (
                        <a
                          href={place.website_uri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Website
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <button type="button" onClick={handleBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Not the right place? Search again
                </button>

                {/* User fields */}
                <div className="space-y-3">
                  <input
                    type="text"
                    value={suggesterName}
                    onChange={e => setSuggesterName(e.target.value)}
                    placeholder="Your name (optional)"
                    className={inputClass}
                  />
                  <input
                    type="email"
                    value={suggesterEmail}
                    onChange={e => setSuggesterEmail(e.target.value)}
                    placeholder="Your email (optional)"
                    className={inputClass}
                  />
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Why do you recommend this spot?"
                    rows={3}
                    className={cn(inputClass, 'resize-none')}
                  />
                </div>

                {submitError && (
                  <p className="text-destructive text-sm">{submitError}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-medium transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Submit suggestion
                </button>
              </form>
            ) : null}
          </div>
        )}

        {/* Step 2 — Success */}
        {step === 2 && (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-5">
              <Check className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Thanks for the suggestion!</h2>
            <p className="text-muted-foreground text-sm mb-6">We'll review it and add it to Qwip if it's a good fit.</p>
            <button onClick={handleReset} className="text-primary font-medium text-sm hover:underline">
              Suggest another spot
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
